import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtIDR, FUEL_META, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Download, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const LabaRugi = () => {
  const { user } = useAuth();
  const [start, setStart] = useState(new Date().toISOString().slice(0, 7) + "-01");
  const [end, setEnd] = useState(todayISO());
  const [data, setData] = useState(null);
  const [exps, setExps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "Gaji Karyawan", amount: "", date: todayISO(), note: "" });

  const load = async () => {
    const r = await api.get("/reports/laba-rugi", { params: { start, end } });
    setData(r.data);
    const e = await api.get("/expenses"); setExps(e.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  const addExp = async (e) => {
    e.preventDefault();
    await api.post("/expenses", { ...form, amount: parseFloat(form.amount) });
    toast.success("Beban tersimpan"); setOpen(false);
    setForm({ category: "Gaji Karyawan", amount: "", date: todayISO(), note: "" });
    load();
  };

  const download = () => {
    const token = localStorage.getItem("spbu_token");
    fetch(`${API}/export/laba-rugi.xlsx?start=${start}&end=${end}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(b => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = "laba_rugi.xlsx"; a.click();
      });
  };

  if (!data) return <div className="text-slate-500">Memuat...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Laporan Laba Rugi</h1>
          <p className="text-sm text-slate-500 mt-1">Profit & Loss Statement</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">Dari</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>
          <div><Label className="text-xs">Sampai</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          <Button data-testid="export-pdf-button" onClick={() => {
            const token = localStorage.getItem("spbu_token");
            const outlet = localStorage.getItem("spbu_active_outlet") || "default";
            fetch(`${API}/export/laba-rugi.pdf?start=${start}&end=${end}`, { headers: { Authorization: `Bearer ${token}`, "X-Outlet-Id": outlet } })
              .then(r => r.blob()).then(b => {
                const url = URL.createObjectURL(b);
                const a = document.createElement("a"); a.href = url; a.download = "laba_rugi.pdf"; a.click();
              });
          }} variant="outline"><Download className="w-4 h-4 mr-2" />PDF</Button>
          <Button onClick={download} className="bg-[#005DAA] hover:bg-[#004885]"><Download className="w-4 h-4 mr-2" />Excel</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          ["Pendapatan", data.revenue, "text-emerald-700"],
          ["HPP BBM", data.hpp, "text-slate-700"],
          ["Laba Kotor", data.gross_profit, "text-blue-700"],
          ["Laba Bersih", data.net_profit, "text-[#E30613]"],
        ].map(([l, v, c]) => (
          <Card key={l}><CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{l}</div>
            <div className={`text-2xl font-extrabold font-mono-fig mt-2 ${c}`}>{fmtIDR(v)}</div>
          </CardContent></Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Rincian Laba Rugi</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <Row label="Pendapatan Penjualan BBM" value={data.revenue} strong />
            <Row label="HPP BBM" value={-data.hpp} />
            <div className="border-t pt-2"><Row label="Laba Kotor" value={data.gross_profit} strong /></div>
            <div className="pt-3 border-t">
              <div className="text-sm font-semibold text-slate-700 mb-1">Beban Operasional</div>
              {Object.entries(data.expense_breakdown).map(([k, v]) => <Row key={k} label={`  ${k}`} value={-v} />)}
              <Row label="Total Beban Operasional" value={-data.operating_expenses} />
              <Row label="Penyusutan Fiskal" value={-data.depreciation} />
            </div>
            <div className="border-t pt-2"><Row label="Laba Bersih" value={data.net_profit} strong big /></div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Margin per Jenis BBM</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead>BBM</TableHead><TableHead className="text-right">Liter</TableHead>
                <TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">Margin</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {Object.entries(data.per_fuel).map(([code, v]) => {
                  const m = FUEL_META[code];
                  return (
                    <TableRow key={code}>
                      <TableCell><span className={`inline-flex items-center gap-2`}><span className={`w-2 h-2 rounded-full ${m.dot}`}></span>{m.name}</span></TableCell>
                      <TableCell className="text-right font-mono-fig">{v.liters.toLocaleString("id-ID")}</TableCell>
                      <TableCell className="text-right font-mono-fig">{fmtIDR(v.revenue)}</TableCell>
                      <TableCell className="text-right font-mono-fig text-emerald-700">{fmtIDR(v.revenue - v.hpp)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Daftar Beban Operasional</CardTitle>
          {user?.role === "admin" && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button size="sm" className="bg-[#E30613] hover:bg-[#C8040F]"><Plus className="w-4 h-4 mr-1" />Tambah Beban</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Tambah Beban Operasional</DialogTitle></DialogHeader>
                <form onSubmit={addExp} className="space-y-4">
                  <div><Label>Kategori</Label>
                    <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}>
                      {["Gaji Karyawan","Listrik & Air","Maintenance","Sewa","Administrasi","Bahan Habis Pakai","Lain-lain"].map(x => <option key={x}>{x}</option>)}
                    </select>
                  </div>
                  <div><Label>Nominal</Label><Input type="number" value={form.amount} onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))} required /></div>
                  <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} required /></div>
                  <div><Label>Catatan</Label><Input value={form.note} onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))} /></div>
                  <DialogFooter><Button type="submit" className="bg-[#E30613] hover:bg-[#C8040F]">Simpan</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Kategori</TableHead><TableHead className="text-right">Jumlah</TableHead><TableHead>Catatan</TableHead>{user?.role === "admin" && <TableHead></TableHead>}</TableRow></TableHeader>
            <TableBody>
              {exps.map(e => (
                <TableRow key={e.id}>
                  <TableCell className="font-mono-fig text-sm">{e.date}</TableCell>
                  <TableCell>{e.category}</TableCell>
                  <TableCell className="text-right font-mono-fig">{fmtIDR(e.amount)}</TableCell>
                  <TableCell className="text-sm text-slate-500">{e.note}</TableCell>
                  {user?.role === "admin" && <TableCell><Button size="icon" variant="ghost" onClick={async () => { await api.delete(`/expenses/${e.id}`); load(); }}><Trash2 className="w-4 h-4 text-rose-600" /></Button></TableCell>}
                </TableRow>
              ))}
              {exps.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-6">Belum ada beban</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

const Row = ({ label, value, strong, big }) => (
  <div className={`flex items-center justify-between ${big ? "text-lg" : "text-sm"} ${strong ? "font-bold" : ""}`}>
    <span className={strong ? "text-slate-900" : "text-slate-600"}>{label}</span>
    <span className={`font-mono-fig ${value < 0 ? "text-rose-700" : strong ? "text-slate-900" : "text-slate-700"}`}>{fmtIDR(value)}</span>
  </div>
);

export default LabaRugi;
